import { useEffect, useState } from "react";

import { useLocation, useNavigate, useParams } from "react-router-dom";

import { RadioGroup } from "@headlessui/react";
import { BeakerIcon, CodeBracketIcon } from "@heroicons/react/24/outline";

import LeadContactDetails from "../../components/contacts/LeadContactDetails";
import DuplicateVehicleBanner from "../../components/leads/DuplicateVehicleBanner";
import LeadDocuments from "../../components/leads/LeadDocuments";
import DriverNotes from "../../components/leads/LeadInformation/DriverNotes";
import LeadPaymentDetails from "../../components/leads/LeadInformation/LeadPaymentDetails";
import LeadStatusCard from "../../components/leads/LeadInformation/LeadStatusCard";
import OfficeNotes from "../../components/leads/LeadInformation/OfficeNotes";
import VehicleNotes from "../../components/leads/LeadInformation/VehicleNotes";
import LeadParts from "../../components/leads/LeadParts/LeadParts";
import LeadSMS from "../../components/leads/LeadSMS";
import useNotification from "../../components/notifications/useNotifications";
import Breadcrumbs from "../../components/shared/Breadcrumbs/Breadcrumbs";
import ConfirmModal from "../../components/shared/Confirm/ConfirmModal";
import LoadingWheel from "../../components/shared/LoadingWheel";
import EnginesWorldwideDetails from "../../components/vehicles/EnginesWorldwideDetails";
import VehicleDetails from "../../components/vehicles/VehicleDetails";
import VehicleLocation from "../../components/vehicles/VehicleLocation";
import { usePageNameContext } from "../../contexts/PageTitleContext";
import useSingleLead from "../../data/useSingleLead";
import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";
import { ILead } from "../../models/Lead";
import classNames from "../../utils/classNames";
import getAddress from "../../utils/getAddress";
import { enteredRegIfDifferent } from "../../utils/leadRegs";
import LeadIdentityImages from "../../components/leads/LeadInformation/LeadIdentityImages";
import { useAuth } from "../../contexts/AuthContext";
import QuoteDetails from "../../components/shared/Quotes/QuoteDetails";
import QuoteCalculationCard from "../../components/quotes/QuoteCalculationCard";
import useCustomer from "../../data/useCustomer";
import { wilmaSAVCustomerId } from "../../config";
import SimulatePricesModal from "../../components/quotes/breakdown/SimulatePricesModal";
import QuoteBreakdownModal from "../../components/quotes/breakdown/QuoteBreakdownModal";

export default function Lead() {
  const { claims, customerId } = useAuth();
  // Not while impersonating: the API sees the impersonated customer and would refuse.
  const isSav = customerId === wilmaSAVCustomerId;
  const [simulateOpen, setSimulateOpen] = useState(false);
  const [breakdownOpen, setBreakdownOpen] = useState(false);
  const [driver, setDriver] = useState<boolean>(false);
  const navigate = useNavigate();
  const { addNotification } = useNotification();
  const { saveHandlers } = useDefaultCRUDHandlers("Lead");
  const { setInfo } = usePageNameContext();
  const { leadId, tab } = useParams();
  const { state } = useLocation();
  const { lead: singleLead, remove, resendWebhook } = useSingleLead(leadId!);
  const { hasWilmaPro } = useCustomer();
  const [lead, setLead] = useState<ILead | undefined>();
  const [loading, setLoading] = useState(true);
  const [leadName, setLeadName] = useState<string | undefined>("");
  const [open, setOpen] = useState(false);
  const [resendWebhookOpen, setResendWebhookOpen] = useState(false);
  const [viewType, setViewType] = useState(0);
  const viewTypes = [
    { value: 0, label: "Lead Information" },
    { value: 1, label: "Documents" },
    // { value: 2, label: "SMS/Email" },
  ];
  const [isDragging, setIsDragging] = useState(false);
  const [droppedFiles, setDroppedFiles] = useState<File[]>([]);

  const handleResendWebhook = () => {
    resendWebhook.mutate(leadId!, {
      onSuccess: () => {
        addNotification({ variant: "success", primaryText: "Lead sent to Wilma Pro" });
        setResendWebhookOpen(false);
      },
      onError: (error: any) => {
        addNotification({ variant: "error", primaryText: error?.message ?? "Failed to send lead to Wilma Pro" });
        setResendWebhookOpen(false);
      },
    });
  };

  const handleDelete = () => {
    remove.mutate(leadId!, {
      onSuccess: () => {
        saveHandlers.onSuccess();
        navigate(-1);
      },
      onError: (error) => {
        console.log(error);
        addNotification({
          variant: "error",
          primaryText: error,
        });
      },
    });
  };

  useEffect(() => {
    if (leadName) {
      const entered = enteredRegIfDifferent(lead);
      setInfo({
        name: `${leadName}'s Lead ${lead?.vehicle?.vrm ? ` - ${lead.vehicle.vrm}` : ""
          }${entered ? ` (entered as ${entered})` : ""}`,
        desc: `View and manage ${leadName}'s Lead`,
      });
    }
  }, [ leadName]);

  useEffect(() => {
    if (claims) {
      if (claims.isAdmin) {
        
      } else if (claims.isStaff) {
       
      } else if (claims.isDriver) {
        setDriver(true);
      }
    }
  }, [claims]);

  useEffect(() => {
    if (!singleLead.isLoading) {

      if (singleLead.data?.data) {
        setLead(singleLead.data?.data);
        setLeadName(singleLead.data?.data.contact?.fullname);
        setLoading(false);
      } else {
        addNotification({
          variant: "error",
          primaryText: "Lead not found",
        });
        navigate(-1);
      }
    }
  }, [leadId, singleLead.isLoading, singleLead.data, navigate]);

  useEffect(() => {
    const onDragOver = (e: DragEvent) => {
      e.preventDefault();
      e.dataTransfer!.dropEffect = "copy";
    };
    const onDragEnter = (e: DragEvent) => {
      e.preventDefault();
      setIsDragging(true);
    };
    const onDragLeave = (e: DragEvent) => {
      e.preventDefault();
      // only turn off when leaving viewport
      if ((e.target as Element).nodeName === "HTML") {
        setIsDragging(false);
      }
    };
    const onDrop = (e: DragEvent) => {
      e.preventDefault();
      setIsDragging(false);
      const files = Array.from(e.dataTransfer!.files);
      setDroppedFiles(files);
      // switch to “Documents” tab if not already
      if (viewType !== 1) setViewType(1);
    };

    window.addEventListener("dragover", onDragOver);
    window.addEventListener("dragenter", onDragEnter);
    window.addEventListener("dragleave", onDragLeave);
    window.addEventListener("drop", onDrop);
    return () => {
      window.removeEventListener("dragover", onDragOver);
      window.removeEventListener("dragenter", onDragEnter);
      window.removeEventListener("dragleave", onDragLeave);
      window.removeEventListener("drop", onDrop);
    };
  }, [viewType]);

  const pages = [
    { to: "..", label: "Leads", current: false },
    {
      to: leadId && lead ? `/leads/${leadId}` : "",
      label: leadId && lead ? `Lead for ${lead.contact?.fullname}` : "New Lead",
      current: true,
    },
  ];

  return loading ? (
    <div className="mt-8 min-h-[50vh] my-4 p-4 bg-white rounded-lg shadow">
      <LoadingWheel />
    </div>
  ) : (
    <>
      {isDragging && (
        <div
          className="fixed inset-0 flex items-center justify-center pointer-events-none bg-black bg-opacity-50 z-50"
        >
          <div className="text-white text-2xl">Drop files to upload</div>
        </div>
      )}
      <ConfirmModal
        title="Delete Lead?"
        message="Are you sure you want to delete this lead? This is a permanent action."
        confirmButtonText={null}
        onConfirm={handleDelete}
        open={open}
        setOpen={setOpen}
        isLoading={false}
      />
      <ConfirmModal
        title="Resend to Wilma Pro?"
        message="This will resend the lead to Wilma Pro via webhook. Continue?"
        confirmButtonText="Resend"
        onConfirm={handleResendWebhook}
        open={resendWebhookOpen}
        setOpen={setResendWebhookOpen}
        isLoading={resendWebhook.isLoading}
      />
      <Breadcrumbs pages={pages} />

      {lead && <DuplicateVehicleBanner lead={lead} />}

      <RadioGroup value={viewType} onChange={setViewType} className="my-4">
        <RadioGroup.Label className="sr-only">
          Choose which view to display
        </RadioGroup.Label>
        <div className="grid grid-cols-2 lg:grid-cols-2 gap-3">
          {viewTypes.map((type) => (
            <RadioGroup.Option
              key={`${type.value}`}
              value={type.value}
              className={({ active, checked }) =>
                classNames(
                  active ? "ring-2 ring-offset-2 ring-gp-blue-500" : "",
                  checked
                    ? "bg-primary-600 border-transparent text-white hover:bg-gp-blue-700"
                    : "bg-white border-gray-200 text-gray-900 hover:bg-gray-50",
                  "cursor-pointer focus:outline-none border rounded-md py-1.5 px-3 flex items-center justify-center text-sm font-medium uppercase sm:flex-1"
                )
              }
            >
              <RadioGroup.Label as="span">{type.label}</RadioGroup.Label>
            </RadioGroup.Option>
          ))}
        </div>
      </RadioGroup>

      {{
        0: (
          <div className="mt-4 flex flex-col md:grid md:grid-cols-3 gap-6">
            {/* Left hand thin column */}
            <div className="col-span-1 gap-6 flex flex-col">
              <div className="col-span-1 bg-white rounded-lg shadow">
                <iframe
                  className="min-h-[40vh] md:min-h-[50vh]"
                  title="Google map"
                  style={{ border: 0, borderRadius: "0.5rem" }}
                  loading="lazy"
                  src={`https://www.google.com/maps/embed/v1/place?key=AIzaSyAJWEQovZtn6KkghJ5z6maiL0G6KHjwbS4&q=${getAddress(
                    lead?.vehicle
                  )}&zoom=9`}
                  width="100%"
                  height="100%"
                ></iframe>
              </div>

              <VehicleDetails vehicle={lead?.vehicle!} />
              {lead?.quote && (
                <div className="flex flex-col gap-2">
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={() => navigate(`/sellavehicle/${lead.quote!.id}`)}
                      className="inline-flex items-center gap-1 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
                    >
                      View Quote
                    </button>
                    {isSav && (
                      <>
                        <button
                          onClick={() => setSimulateOpen(true)}
                          className="inline-flex items-center gap-1 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
                        >
                          <BeakerIcon className="h-4 w-4 text-purple-600" />
                          Simulate prices
                        </button>
                        <button
                          onClick={() => setBreakdownOpen(true)}
                          className="inline-flex items-center gap-1 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
                        >
                          <CodeBracketIcon className="h-4 w-4 text-blue-600" />
                          Quote breakdown
                        </button>
                        <SimulatePricesModal quoteId={lead.quote.id} open={simulateOpen} setOpen={setSimulateOpen} />
                        <QuoteBreakdownModal quote={lead.quote} open={breakdownOpen} setOpen={setBreakdownOpen} />
                      </>
                    )}
                  </div>
                  <QuoteDetails quote={lead.quote} lead={lead} cols="sm:grid-cols-1 divide-y !gap-[0px]" />
                </div>
              )}
              {/* {lead?.vehicle?.enginesWorldwide.map((offer) => <EnginesWorldwideDetails offer={offer} />)} */}
            </div>

            {/* Right hand wide column */}
            <div className="col-span-2 gap-6 flex flex-col">
              <LeadContactDetails lead={lead!} />
              <VehicleLocation vehicle={lead?.vehicle!} />
              {!driver && <LeadStatusCard leadId={lead?.id!} />}
              {/* <LeadIdentityImages lead={lead!} /> */}
              {!driver && <LeadPaymentDetails lead={lead!} />}

              {/* -------------------- */}
              <div className="grid grid-cols-1 gap-x-4 gap-y-4 lg:grid-cols-2">
                {/* customer id is null if no current customer */}
                {lead?.quote?.collectedCustomerId && (
                  <QuoteCalculationCard
                    calculationJson={lead?.quote?.collectedCalculationJson}
                    quoteType={lead?.quote?.collectedType}
                    isCollection={true}
                    total={lead?.quote?.priceCollected}
                  />
                )}

                {lead?.quote?.dropOffCustomerId && (
                  <QuoteCalculationCard
                    calculationJson={lead?.quote?.dropOffCalculationJson}
                    quoteType={lead?.quote?.dropOffType}
                    isCollection={false}
                    total={lead?.quote?.priceDropOff}
                  />
                )}
              </div> 
            </div>
            <div>
              <OfficeNotes lead={lead!} />
            </div>
            <div>
              <DriverNotes lead={lead!} />
            </div> 
            <div>
              <VehicleNotes lead={lead!} />
              <div className="mt-6 flex flex-col sm:flex-row sm:justify-end gap-3">
                {hasWilmaPro.data?.data && (
                  <button
                    className="rounded-md border border-transparent bg-blue-600 px-3 py-3 text-sm font-medium leading-4 text-white shadow-sm hover:bg-blue-700"
                    type="button"
                    onClick={() => setResendWebhookOpen(true)}
                  >
                    Resend to Wilma Pro
                  </button>
                )}
                <button
                  className="rounded-md border border-transparent bg-red-600 px-3 py-3 text-sm font-medium leading-4 text-white shadow-sm hover:bg-red-700"
                  type="button"
                  onClick={() => setOpen(true)}
                >
                  Delete Lead
                </button>
              </div>
            </div>
          </div>
        ),
        1: <LeadDocuments lead={lead!} droppedFiles={droppedFiles} setDroppedFiles={setDroppedFiles} />,
        // 2: <LeadSMS lead={lead!} />,
      }[viewType]}
    </>
  );
}
